import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-md transition-colors duration-150 ease-out hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
  return <section className="border-t border-border bg-surface py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-93" data-mp="3vp8x25mlb6fe">
      <div className="mx-auto max-w-6xl px-4" data-id="element-94" data-mp="3qqyyrm5kjppk">
        <div className="text-center" data-id="element-95" data-mp="3qo30txtq1114">
          <p className="text-sm font-semibold text-primary" data-id="element-96" data-mp="1p0gmaz0v4c36">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-2 max-w-4xl text-balance text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl" data-id="element-97" data-mp="1trgj849zeu5o">
            Discover What&apos;s Inside <span className="text-primary" data-id="element-98" data-mp="1f0pskzk4m145">{productShortName}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-muted" data-id="element-99" data-mp="1k1wysu6mes8p">
            Visual pages with clear process diagrams, cutaways, worked examples, technical warnings and field-ready
            takeaways. Here are some real pages from Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-100" data-mp="1yq06q9484e05">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] [-webkit-overflow-scrolling:touch] pb-2 [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-101" data-mp="22qbrdlwl6ebm">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-xl border bg-background shadow-md transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-primary opacity-100' : 'border-border opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-102" data-mp="jrvxwgrzut2n" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full object-cover" data-id="element-103" data-mp="1265g0bpejdw" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClass} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-104" data-mp="asilpgchjqe8">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-105" data-mp="61abii3c0100" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClass} right-0 sm:-right-2`} aria-label="Next page" data-id="element-106" data-mp="3egu1974sn1ab">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="39iiu545vv4vs" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-108" data-mp="2cpays92bxk3d">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${index === active ? 'w-6 bg-primary' : 'w-2 bg-border hover:bg-muted'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-109" data-mp="2gt8x4hjtyqvd" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}